import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { planStatusLabel } from '../platform/planConstants';

export default function TenantPlanDetailPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/plans/${planId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setPlan(json.plan);
    } catch (e) {
      setError(e.message);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !plan) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate('/tenant/plans')}>Back to plans</Button>
      </Box>
    );
  }

  if (!plan) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{plan.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            <code>{plan.code}</code>
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/tenant/plans')}>
          Back
        </Button>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Chip label={planStatusLabel(plan.status)} sx={{ mb: 2 }} />
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {row('Description', plan.description || '—')}
          {row('Monthly price', Number(plan.monthly_price).toFixed(2))}
          {row('Annual price', Number(plan.annual_price).toFixed(2))}
          {row('API call limit', plan.api_call_limit)}
          {row('Storage limit (MB)', plan.storage_limit_mb)}
          {row('Max users', plan.max_users)}
          {row('Max API keys', plan.max_api_keys)}
          {row('Trial days', plan.trial_days)}
        </Grid>
      </Paper>
    </Box>
  );
}
